
import { computed, defineComponent, onMounted, reactive, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'

import SwooshArrow from '@/components/catering/SwooshArrow.vue'
import TermsAgreement from '@/components/TermsAgreement.vue'
import { TERMS_ACCEPTED_PARAMS } from '@/config/legal'
import { logInWithToken } from '@/helpers/tokenLogin'

const RESET_LINK_ERROR = 'This link has expired or was already used. Request a new one from the login page.'
const NETWORK_ERROR = 'Something went wrong. Please try again.'
const NEW_LINK_SENT = "We've emailed you a new link. It works for 24 hours."

const EMAIL_ME_A_NEW_LINK = 'Email me a new link'

const DEAD_LINKS = {
  expired: {
    title: 'Link expired',
    heading: 'This link has expired.',
    text: 'Password links work for 24 hours. We can email you a new one.',
    action: EMAIL_ME_A_NEW_LINK
  },
  used: {
    title: 'Link already used',
    heading: 'This link has already been used.',
    text: 'It was used to set a password, or a newer link was sent after it. We can email you a new one.',
    action: EMAIL_ME_A_NEW_LINK
  },
  invalid: {
    title: 'Link not valid',
    heading: "This link isn't valid.",
    text: 'Make sure you opened the whole link from the email, or request a new one.',
    action: 'Request a new link'
  }
}

const callResetApi = (path, options = {}) =>
  fetch(`${process.env.VUE_APP_API_SERVER}/${path}`, {
    ...options,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    }
  }).then(async response => ({ response, body: await response.json().catch(() => ({})) }))

export default defineComponent({
  components: { SwooshArrow, TermsAgreement },
  setup() {
    const store = useStore()
    const router = useRouter()
    const route = useRoute()
    const error = ref('')
    const linkStatus = ref('checking')
    const newLinkSent = ref(false)
    const sendingNewLink = ref(false)
    const data = reactive({
      password: '',
      password_confirmation: ''
    })

    const resetToken = () => route.query.token || ''
    const encodedToken = () => encodeURIComponent(resetToken())

    const deadLink = computed(() => DEAD_LINKS[linkStatus.value] || null)

    const passwordMismatch = computed(() => {
      return data.password && data.password_confirmation && data.password !== data.password_confirmation
    })

    const isFormValid = computed(() => {
      return data.password && data.password_confirmation && !passwordMismatch.value
    })

    const checkLink = () => {
      if (!resetToken()) {
        linkStatus.value = 'invalid'
        return
      }

      callResetApi(`system_users_password_reset/status?token=${encodedToken()}`)
        .then(({ response, body }) => {
          linkStatus.value = (response.ok && body?.link_status) || 'valid'
        })
        .catch(() => {
          linkStatus.value = 'valid'
        })
    }

    const formSubmitted = () => {
      if (linkStatus.value !== 'valid' || !isFormValid.value) return
      error.value = ''

      callResetApi(`system_users_password_reset?token=${encodedToken()}`, {
        method: 'POST',
        body: JSON.stringify({
          ...TERMS_ACCEPTED_PARAMS,
          system_user: {
            password: data.password
          }
        })
      })
        .then(async ({ response, body }) => {
          if (!response.ok) {
            if (DEAD_LINKS[body?.link_status]) {
              linkStatus.value = body.link_status
              return
            }
            error.value = body?.errors?.[0] || RESET_LINK_ERROR
            return
          }
          const loggedIn = body?.token && (await logInWithToken(body.token, { store, router }).catch(() => false))
          if (!loggedIn) router.push('/login?reset-success')
        })
        .catch(() => {
          error.value = NETWORK_ERROR
        })
    }

    const requestNewLink = () => {
      if (linkStatus.value === 'invalid') {
        router.push('/forgot-password-email')
        return
      }

      error.value = ''
      sendingNewLink.value = true
      callResetApi('system_users_password_reset_request', {
        method: 'POST',
        body: JSON.stringify({ token: resetToken() })
      })
        .then(({ response }) => {
          if (response.ok) newLinkSent.value = true
          else error.value = NETWORK_ERROR
        })
        .catch(() => {
          error.value = NETWORK_ERROR
        })
        .finally(() => {
          sendingNewLink.value = false
        })
    }

    const loginClicked = () => {
      router.push('/login')
    }

    onMounted(checkLink)

    return {
      formSubmitted,
      data,
      error,
      linkStatus,
      deadLink,
      newLinkSent,
      sendingNewLink,
      requestNewLink,
      loginClicked,
      passwordMismatch,
      isFormValid,
      NEW_LINK_SENT
    }
  }
})
